/**
 * ProgramSwitcher.js — Sprint 1 Workstream B
 *
 * The two-card chooser shown after sign-in. Lets the user pick the
 * MOSAIC product line they want to operate in. Auto-bypassed for
 * single-licensed users by the page that mounts this component.
 *
 * Design contract: /app/design_guidelines.md §5 (Login → Program Switcher)
 */
import { BookOpen, ShieldCheck, Lock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProgram, PROGRAMS } from "../../contexts/ProgramContext";

const ICON_MAP = { BookOpen, ShieldCheck };

export default function ProgramSwitcher({
  availablePrograms = ["practice"],
  redirectPath = "/",
  showHeader = true,
  onChosen,
}) {
  const { switchProgram } = useProgram();
  const navigate = useNavigate();

  const choose = (programId) => {
    if (!availablePrograms.includes(programId)) return;
    switchProgram(programId);
    if (onChosen) onChosen(programId);
    else navigate(redirectPath);
  };

  return (
    <div
      className="min-h-screen bg-mosaic-bg flex flex-col items-center justify-center px-6 py-12 font-body"
      data-testid="program-switcher-root"
    >
      {showHeader && (
        <div className="text-center mb-10 max-w-2xl">
          <div className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-mosaic-brand bg-sky-50 border border-sky-200 rounded-full px-3 py-1.5 mb-5">
            Aligned to WIDA® ELD Standards 2020
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-mosaic-ink leading-tight mb-3">
            Choose your MOSAIC program
          </h1>
          <p className="text-mosaic-ink-2 text-base sm:text-lg">
            You can switch programs anytime from the header. Each program has its own content, dashboards, and reports.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl w-full">
        {Object.values(PROGRAMS).map((p) => {
          const Icon = ICON_MAP[p.icon] || BookOpen;
          const available = availablePrograms.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => choose(p.id)}
              disabled={!available}
              data-testid={`program-switcher-card-${p.id}`}
              className={`group text-left bg-white border border-mosaic-border-soft rounded-xl p-8 transition-all duration-150 relative overflow-hidden ${
                available
                  ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
                  : "opacity-70 cursor-not-allowed"
              }`}
            >
              <div className={`absolute top-0 left-0 right-0 h-1 ${p.bgClass}`} aria-hidden="true" />
              <div className="flex items-center justify-between mb-5">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${p.bgLightClass} ${p.accentClass}`}
                  aria-hidden="true"
                >
                  <Icon className="w-6 h-6" />
                </div>
                {!available && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                    <Lock className="w-3 h-3" /> Request Upgrade
                  </span>
                )}
              </div>
              <h2 className="font-serif text-2xl text-mosaic-ink mb-2">{p.name}</h2>
              <p className="text-sm text-mosaic-ink-3 mb-4">{p.tagline}</p>
              <p className="text-sm text-mosaic-ink-2 leading-relaxed mb-6">{p.description}</p>
              <div
                className={`inline-flex items-center gap-2 text-sm font-medium ${p.accentClass} transition-transform group-hover:translate-x-0.5`}
              >
                {available ? "Enter program" : "Request access"}
                <ArrowRight className="w-4 h-4" />
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-mosaic-ink-3 mt-8 text-center max-w-2xl">
        MOSAIC is an independent platform. Not affiliated with, endorsed by, or certified by WIDA®.
      </p>
    </div>
  );
}
